import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle, WhatsappLogo, PaperPlaneTilt } from "@phosphor-icons/react";
import { submitEnquiry } from "@/lib/api";
import { openWhatsApp } from "@/lib/site";
import { PRODUCTS, localizeProduct } from "@/lib/products";
import { useLang } from "@/lib/i18n";

const STATES = [
  "Maharashtra", "Gujarat", "Rajasthan", "Madhya Pradesh", "Karnataka",
  "Tamil Nadu", "Andhra Pradesh", "Telangana", "Uttar Pradesh", "Punjab",
  "Haryana", "Bihar", "West Bengal", "Kerala", "Odisha", "Chhattisgarh",
  "Jharkhand", "Delhi", "Other",
];

const EMPTY = {
  name: "", company: "", phone: "", email: "", city: "",
  state: "", product: "", requirement: "", message: "",
};

const field =
  "w-full rounded-lg border border-hairline bg-surface px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink/40 focus:border-forest focus:ring-1 focus:ring-forest";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink/60";

export const EnquiryForm = ({ defaultProduct = "" }) => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ ...EMPTY, product: defaultProduct });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(t("form.validation"));
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry(form);
      setDone(true);
      toast.success(t("form.successToast"));
    } catch (err) {
      toast.error(t("form.errorToast"));
    } finally {
      setLoading(false);
    }
  };

  const waMessage = () => {
    const p = form.product ? ` regarding ${form.product}` : "";
    return `Hello RK Jain,\nI am ${form.name || "a customer"}${p}.\n${form.message || "Please share more details."}`;
  };

  if (done) {
    return (
      <div
        className="flex flex-col items-center justify-center rounded-2xl border border-hairline bg-surface p-10 text-center shadow-sm md:p-16"
        data-testid="enquiry-success"
      >
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-forest/10 text-forest">
          <CheckCircle size={48} weight="fill" className="text-emerald" />
        </div>
        <h3 className="mt-6 font-heading text-3xl font-bold text-forest">{t("form.thankYou")}</h3>
        <p className="mt-3 max-w-md text-ink/70">{t("form.thankYouText")}</p>
        <button
          onClick={() => openWhatsApp(waMessage())}
          data-testid="continue-whatsapp-button"
          className="btn-gold mt-8"
        >
          <WhatsappLogo size={20} weight="fill" /> {t("form.continueWhatsapp")}
        </button>
        <button
          onClick={() => {
            setForm({ ...EMPTY });
            setDone(false);
          }}
          className="mt-4 text-sm font-medium text-ink/50 underline hover:text-forest"
          data-testid="new-enquiry-button"
        >
          {t("form.newEnquiry")}
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-hairline bg-surface p-6 shadow-sm md:p-10"
      data-testid="enquiry-form"
    >
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div>
          <label className={labelCls}>{t("form.name")} *</label>
          <input className={field} value={form.name} onChange={set("name")}
            placeholder={t("form.namePh")} data-testid="enquiry-name" required />
        </div>
        <div>
          <label className={labelCls}>{t("form.company")}</label>
          <input className={field} value={form.company} onChange={set("company")}
            placeholder={t("form.companyPh")} data-testid="enquiry-company" />
        </div>
        <div>
          <label className={labelCls}>{t("form.phone")} *</label>
          <input className={field} value={form.phone} onChange={set("phone")}
            placeholder={t("form.phonePh")} data-testid="enquiry-phone" required />
        </div>
        <div>
          <label className={labelCls}>{t("form.email")}</label>
          <input type="email" className={field} value={form.email} onChange={set("email")}
            placeholder={t("form.emailPh")} data-testid="enquiry-email" />
        </div>
        <div>
          <label className={labelCls}>{t("form.city")}</label>
          <input className={field} value={form.city} onChange={set("city")}
            placeholder={t("form.cityPh")} data-testid="enquiry-city" />
        </div>
        <div>
          <label className={labelCls}>{t("form.state")}</label>
          <select className={field} value={form.state} onChange={set("state")} data-testid="enquiry-state">
            <option value="">{t("form.selectState")}</option>
            {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>{t("form.product")}</label>
          <select className={field} value={form.product} onChange={set("product")} data-testid="enquiry-product">
            <option value="">{t("form.selectProduct")}</option>
            {PRODUCTS.map((p) => (
              <option key={p.slug} value={p.name}>{localizeProduct(p, lang).name}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>{t("form.requirement")}</label>
          <input className={field} value={form.requirement} onChange={set("requirement")}
            placeholder={t("form.reqPh")} data-testid="enquiry-requirement" />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>{t("form.message")}</label>
          <textarea rows={4} className={field} value={form.message} onChange={set("message")}
            placeholder={t("form.msgPh")} data-testid="enquiry-message" />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="btn-gold mt-8 w-full justify-center disabled:opacity-60"
        data-testid="enquiry-submit"
      >
        <PaperPlaneTilt size={20} weight="fill" />
        {loading ? t("form.submitting") : t("form.submit")}
      </button>
    </form>
  );
};

export default EnquiryForm;
